/*
    math tools/helpers
*/
import { teaHash } from '@play-co/replicant';

// types
//-----------------------------------------------------------------------------
export type RandomFunction = () => number;

// constants
//-----------------------------------------------------------------------------
const PI2 = Math.PI * 2;

// integer
//-----------------------------------------------------------------------------
export function integerRandom(
  min: number,
  max: number,
  fRandom: RandomFunction = Math.random,
): number {
  return min + Math.floor(fRandom() * (max - min + 1));
}

export function integerTea(
  min: number,
  max: number,
  v0: string | number,
  v1?: number,
): number {
  return min + Math.floor(teaHash(v0, v1) * (max - min + 1));
}

export function randomItem<T>(
  arr: T[],
  fRandom: RandomFunction = Math.random,
): T {
  const index = integerRandom(0, arr.length - 1, fRandom);
  return arr[index];
}

// results a list of unique random integers. the range should be (much) higher than count or infinite loop!
export function integerRandomUniqueList(
  min: number,
  max: number,
  count: number,
  fRandom: RandomFunction = Math.random,
): number[] {
  console.assert(max - min > count);
  const list: number[] = [];
  for (let i = 0; i < count; ++i) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const roll = integerRandom(min, max, fRandom);
      if (!list.includes(roll)) {
        list.push(roll);
        break;
      }
    }
  }
  return list;
}

/*
    takes a list of weights (odds) and returns a random index based on weight
    example:
        weights: [10,0,70,20]
        return: 2 (usually)
*/
export function randomOdds(
  weights: number[],
  fRandom: RandomFunction = Math.random,
): number {
  const total = weights.reduce((t, weight) => t + weight, 0);
  const roll = fRandom() * total;

  // for each odds index
  for (let i = 0, acc = 0; i < weights.length; ++i) {
    acc += weights[i];
    if (roll < acc) return i;
  }

  // rare edge case
  return 0;
}

// float
//-----------------------------------------------------------------------------
export function floatRandom(
  min: number,
  max: number,
  fRandom: RandomFunction = Math.random,
): number {
  return min + fRandom() * (max - min);
}

// numeric
//-----------------------------------------------------------------------------
export function numberInRange(
  value: number,
  min: number,
  max: number,
): boolean {
  return value >= min && value <= max;
}

export function numberClamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function numberRound(value: number, factor: number): number {
  return Math.round(value / factor) * factor;
}

export function numberFloor(value: number, factor: number): number {
  return Math.floor(value / factor) * factor;
}

export function numberContain(value: number, boundary: number): number {
  if (value > boundary) return value - boundary;
  if (value < -boundary) return value + boundary;
  return value;
}

// geometry
//-----------------------------------------------------------------------------
export function angleContain(radians: number): number {
  return numberContain(radians, PI2);
}
